import { useEffect, useState } from 'react';

import {
  Box,
  type BoxProps,
  Button,
  Flex,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';

import { Icon, IconClockStopwatch } from '@/components/Icons';
import { LinkAdmin } from '@/features/admin';
import { useRemainingFreeTrialDays } from '@/features/admin-interview/useRemainingFreeTrialDays';
import { ADMIN_NAV_BAR_HEIGHT } from '@/features/admin/constants';
import { useAuth } from '@/features/auth';
import { NavBarLanguageMenu } from '@/features/common';
import { variantPath } from '@/utils/variant/variantPath';

import { AdminNavBarAccountMenu } from './AdminNavBarAccountMenu';
import { AdminNavBarMainMenu } from './AdminNavBarMainMenu';
import { UpgradeModal } from './UpgradeModal';

export const AdminNavBar = (props: BoxProps) => {
  const { t } = useTranslation(['interview', 'common']);
  const { anonymousShared } = useAuth();
  const [isUpgradeOpen, setUpgradeOpen] = useState(false);
  const { remainingTrialDays, isDemoCompany } = useRemainingFreeTrialDays();
  const location = useLocation();

  useEffect(() => {
    if (
      isDemoCompany &&
      remainingTrialDays <= 0 &&
      location.pathname === '/admin/dashboard'
    ) {
      setUpgradeOpen(true);
    } else {
      setUpgradeOpen(false);
    }
  }, [remainingTrialDays, location.pathname, isDemoCompany]);

  return (
    <Box {...props}>
      <Flex
        zIndex="sticky"
        position="fixed"
        top="0"
        insetStart="0"
        insetEnd="0"
        color="gray.50"
        align="center"
        justify="center"
        pt="safe-top"
        px="4"
        h={ADMIN_NAV_BAR_HEIGHT}
        bg="white"
        borderBottom="1px solid transparent"
      >
        <Flex
          grow={1}
          align="center"
          justify="space-between"
          maxW="container.xl"
        >
          <Box as={LinkAdmin} to="/dashboard" mx={0}>
            {variantPath('Logo')}
          </Box>

          {!anonymousShared && (
            <AdminNavBarMainMenu me="auto" ms="4" display="flex" />
          )}
          <HStack gap={3}>
            {!anonymousShared && (
              <>
                <LinkAdmin to="/interviews/new">
                  <Button
                    variant="@primary"
                    size="md"
                    leftIcon={<Icon icon={LuPlus} fontSize="lg" />}
                    h="12"
                  >
                    {t('interview:createNewInterview.createInterview')}
                  </Button>
                </LinkAdmin>
                <AdminNavBarAccountMenu />
              </>
            )}
            <NavBarLanguageMenu />
          </HStack>
        </Flex>
      </Flex>
      <Box h={ADMIN_NAV_BAR_HEIGHT} />
      {isDemoCompany && (
        <Flex
          py={1}
          borderBottomWidth={1}
          borderColor="primary.100"
          bg="primary.50"
          justifyContent="center"
          alignItems="center"
          columnGap={2}
          color="primary.500"
          fontSize="sm"
        >
          <IconClockStopwatch
            color={remainingTrialDays <= 0 ? 'red.600' : 'primary.500'}
            fontSize={22}
          />
          {remainingTrialDays <= 0 ? (
            <Text color="red.600">{t('common:freeTrialFinished')}</Text>
          ) : (
            <Trans
              i18nKey="remainingTrialDays"
              ns="common"
              values={{ days: remainingTrialDays }}
            />
          )}
          <Button
            variant="link"
            colorScheme="primary"
            fontWeight="bold"
            ml={-1}
            onClick={() => setUpgradeOpen(true)}
          >
            {t('common:actions.getInTouch')}
          </Button>
        </Flex>
      )}
      <UpgradeModal
        title={
          remainingTrialDays <= 0
            ? t('common:trialExpired')
            : t('interview:upgradeModal.title')
        }
        isOpen={isUpgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
    </Box>
  );
};
